import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import {
  ProgressCallback,
  SignedUrlPhotoVideoDoc,
  UploadState,
  UploadVideoPhotoReturnType,
} from '../../components/ProjectManagement/Photos/interface/Photos.interface';
import { UploadFilesToS3 } from '../../components/ProjectManagement/ProjectInfo/helpers/UploadFilesToS3';
import { EVENT_AND_PATH_CONSTANT } from '../../constants';
import { userAcitivityWorkerHelper } from '../../userActivityWorker/helper';

const initialState: UploadState = {
  progress: 0,
  status: 'idle',
  filesInfo: [],
  filesBlob: [],
  fileNameArray: [],
  videoFileName: '',
  preSignedApiResponse: null,
  projectId: null,
};
export const uploadVideoPhoto = createAsyncThunk<
  UploadVideoPhotoReturnType,
  {
    selectedFiles: File[];
    videoFileName: string;
    onProgress: ProgressCallback;
  }
>(
  'upload/video',
  async (
    { selectedFiles, videoFileName, onProgress },
    { dispatch, getState, rejectWithValue }
  ) => {
    let apiStatus = false;

    const state = getState() as { uploadVideoPhoto: UploadState };
    const projectId = state.uploadVideoPhoto.projectId;

    // ---- Prepare file metadata ----
    const formattedFiles: Record<string, string> =
      selectedFiles.length > 0
        ? {
            type: selectedFiles[0].type,
            ...selectedFiles.reduce(
              (acc, file, i) => ({ ...acc, [`Image_name${i + 1}`]: file.name }),
              {}
            ),
          }
        : {};

    try {
      const response = await UploadFilesToS3({
        selectedFiles,
        videoFileName,
        onProgress,
        onPreSignedUrlReady: preSignedApiResponse => {
          if (
            preSignedApiResponse?.success &&
            preSignedApiResponse?.data?.status
          ) {
            dispatch(setPreSignedApiResponse(preSignedApiResponse));
            apiStatus = true;
          }
        },
      });

      apiStatus = true;
      const putSignedUrls: SignedUrlPhotoVideoDoc[] =
        response?.data?.data?.putSignedUrl ?? [];

      const fileNameArray = putSignedUrls.map(
        (item: SignedUrlPhotoVideoDoc) => ({
          fileName: item.fileName,
          originalFileName: item.originalFileName,
        })
      );

      return {
        preSignedApiResponse: { success: true },
        fileNameArray,
      };
    } catch (error) {
      apiStatus = false;
      return rejectWithValue({
        preSignedApiResponse: { success: false, error },
        fileNameArray: [],
      } as UploadVideoPhotoReturnType);
    } finally {
      // ---- Log activity AFTER knowing final apiStatus ----
      userAcitivityWorkerHelper({
        quickSync: true,
        customPayload: {
          action: EVENT_AND_PATH_CONSTANT?.PROJECT_ADD_PHOTO_VIDEOS?.EVENT,
          data: { ...formattedFiles },
          path: EVENT_AND_PATH_CONSTANT?.PROJECT_ADD_PHOTO_VIDEOS?.PATH,
          project_id: projectId,
          api_status: apiStatus,
        },
      });
    }
  }
);

const uploadVideoPhotoSlice = createSlice({
  name: 'uploadPhotoVideoFile',
  initialState,
  reducers: {
    setProgress(state, action) {
      state.progress = action.payload;
    },

    setFilesInfo(state, action) {
      state.filesInfo = action.payload;
    },
    setVideoFileName(state, action) {
      state.videoFileName = action.payload;
    },
    setFilesBlob(state, action) {
      state.filesBlob = action.payload;
    },
    setPreSignedApiResponse(state, action) {
      state.preSignedApiResponse = action.payload;
    },
    setProjectId(state, action) {
      state.projectId = action.payload;
    },
    resetUpload() {
      return { ...initialState };
    },
  },
  extraReducers: builder => {
    builder
      .addCase(uploadVideoPhoto.pending, state => {
        state.status = 'uploading';
      })
      .addCase(uploadVideoPhoto.fulfilled, (state, action) => {
        state.status = 'success';
        state.preSignedApiResponse =
          action.payload?.preSignedApiResponse || null;
        state.fileNameArray = Array.isArray(action.payload?.fileNameArray)
          ? action.payload?.fileNameArray
          : [];
      })
      .addCase(uploadVideoPhoto.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.payload as string;
      });
  },
});

export const {
  setProgress,
  resetUpload,
  setFilesInfo,
  setFilesBlob,
  setVideoFileName,
  setPreSignedApiResponse,
  setProjectId,
} = uploadVideoPhotoSlice.actions;
export const uploadVideoPhotoReducer = uploadVideoPhotoSlice.reducer;
